import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import DatePicker from 'react-multi-date-picker';
import "react-multi-date-picker/styles/backgrounds/bg-dark.css";     // tema scuro picker
import { useNavigate } from 'react-router-dom';
import { toast, Slide } from 'react-toastify';
import { useDashboard } from '../../contexts/DashboardContext';
import api from '../../services/api';
import Loader from '../Utils/Loader';
import { buildPersonOptions, filterPersonOptions, personOptionMatches } from '../../utils/personSelect';
import '../../css/addSchedule.css';

/* ------------------------------------------------------------------ */
/* Costanti                                                            */
/* ------------------------------------------------------------------ */
const daysOptions = [
  { value: 'lunedì', label: 'Lunedì' },
  { value: 'martedì', label: 'Martedì' },
  { value: 'mercoledì', label: 'Mercoledì' },
  { value: 'giovedì', label: 'Giovedì' },
  { value: 'venerdì', label: 'Venerdì' },
  { value: 'sabato', label: 'Sabato' },
  { value: 'domenica', label: 'Domenica' },
];

const initialFormData = {
  programId: '',
  description: '',
  date: '',
  startTime: '',
  endTime: '',
  isRecurring: false,
  priority: 0,        // NEW
  guests: [],
  topics: [],
};

const initialRecurringEntry = {
  dayOfWeek: '',
  startTime: '',
  endTime: '',
  description: '',
  priority: 0,        // NEW
  blacklistDates: [], // NEW
  guests: [],
  topics: [],
};

const timeToMinutes = (value = '') => {
  if (!value) return null;
  const [hoursStr, minutesStr = '0'] = value.split(':');
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

const isTimeRangeValid = (start, end) => {
  const startMinutes = timeToMinutes(start);
  const endMinutes = timeToMinutes(end);
  if (startMinutes === null || endMinutes === null) return false;
  return startMinutes < endMinutes;
};

/* ------------------------------------------------------------------ */
/* Componente                                                          */
/* ------------------------------------------------------------------ */
const AddSchedule = () => {
  const navigate = useNavigate();
  const { people, topics, programs } = useDashboard();

  /* ---------- state ---------- */
  const [programOptions, setProgramOptions] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);

  /* Deriva peopleOptions dal context (si aggiorna automaticamente) */
  const peopleOptions = useMemo(
    () => buildPersonOptions(people),
    [people]
  );
  const [formData, setFormData] = useState({ ...initialFormData });
  const [recurringEntries, setRecurringEntries] = useState([{ ...initialRecurringEntry }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const toastOptions = {
    theme    : 'dark',
    position : 'top-center',
    autoClose: 3500,
    transition: Slide,
  };

  const emitError = (message) => {
    setError(message);
    if (message) {
      toast.error(message, toastOptions);
    }
  };

  /* ---------- fetch select options (usa context dove disponibile) ---------- */
  useEffect(() => {
    // Programs: usa context se disponibile, altrimenti fetch
    if (programs && programs.length > 0) {
      setProgramOptions(
        programs.map(p => ({ value: p.id, label: p.title }))
                .sort((a, b) => a.label.localeCompare(b.label))
      );
    } else {
      api.get('/programs')
         .then(res => setProgramOptions(
           res.data.map(p => ({ value: p.id, label: p.title }))
                  .sort((a, b) => a.label.localeCompare(b.label))
         ))
         .catch(console.error);
    }

    // Topics: usa context se disponibile, altrimenti fetch
    if (topics && topics.length > 0) {
      setTopicOptions(
        topics.map(t => ({ value: t.id, label: t.name }))
              .sort((a, b) => a.label.localeCompare(b.label))
      );
    } else {
      api.get('/topics')
         .then(res => setTopicOptions(
           res.data.map(t => ({ value: t.id, label: t.name }))
                  .sort((a, b) => a.label.localeCompare(b.label))
         ))
         .catch(console.error);
    }
    // peopleOptions è già derivato dal context via useMemo
  }, [programs, topics]);

  /* ---------- handlers comuni ---------- */
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(fd => ({ ...fd, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleProgramSelect = opt =>
    setFormData(fd => ({ ...fd, programId: opt ? opt.value : '' }));

  const handleMultiSelectChange = (selected, field) => {
    const vals = Array.isArray(selected)
      ? selected.map(o => o.value)
      : selected ? [selected.value] : [];
    setFormData(fd => ({ ...fd, [field]: vals }));
  };

  /* ---------- handlers per ricorrenze ---------- */
  const handleRecurringEntryChange = (idx, field, value) =>
    setRecurringEntries(re =>
      re.map((entry, i) => (i === idx ? { ...entry, [field]: value } : entry))
    );

  const handleRecurringMultiSelectChange = (idx, field, selected) => {
    const vals = Array.isArray(selected)
      ? selected.map(o => o.value)
      : selected ? [selected.value] : [];
    setRecurringEntries(re =>
      re.map((entry, i) => (i === idx ? { ...entry, [field]: vals } : entry))
    );
  };

  const addRecurringEntry = () =>
    setRecurringEntries(re => [...re, { ...initialRecurringEntry }]);

  const removeRecurringEntry = idx =>
    setRecurringEntries(re => re.filter((_, i) => i !== idx));

  const loadPeopleOptions = (input, cb) =>
    cb(filterPersonOptions(peopleOptions, input));

  const resetForm = () => {
    setFormData({ ...initialFormData });
    setRecurringEntries([{ ...initialRecurringEntry }]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ---------- submit ---------- */
  const handleSubmit = async e => {
    e.preventDefault();
    emitError('');
    setSuccess('');

    if (!formData.programId) {
      emitError('Seleziona prima un programma.');
      return;
    }

    if (formData.isRecurring) {
      for (let i = 0; i < recurringEntries.length; i += 1) {
        const entry = recurringEntries[i];
        if (!entry.dayOfWeek || !entry.startTime || !entry.endTime) {
          emitError(`Compila giorno e orari per la ricorrenza #${i + 1}.`);
          return;
        }
        if (!isTimeRangeValid(entry.startTime, entry.endTime)) {
          emitError(`L'orario di fine deve seguire l'inizio per la ricorrenza #${i + 1}.`);
          return;
        }
      }
    } else {
      if (!formData.date || !formData.startTime || !formData.endTime) {
        emitError('Compila data e orari per lo scheduling.');
        return;
      }
      if (!isTimeRangeValid(formData.startTime, formData.endTime)) {
        emitError("L'orario di fine deve essere successivo all'inizio.");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (formData.isRecurring) {
        await Promise.all(
          recurringEntries.map(entry =>
            api.post('/schedules', {
              programId: formData.programId,
              isRecurring: true,
              date: null,
              dayOfWeek: entry.dayOfWeek,
              startTime: entry.startTime,
              endTime: entry.endTime,
              description: entry.description,
              priority: entry.priority,
              blacklistDates: entry.blacklistDates,
              guests: entry.guests,
              topics: entry.topics,
            })
          )
        );
        setSuccess('Eventi ricorrenti aggiunti con successo!');
      } else {
        await api.post('/schedules', {
          ...formData,
          isRecurring: false,
          dayOfWeek: null,
          blacklistDates: [],
        });
        setSuccess('Evento aggiunto con successo!');
      }
      resetForm();
    } catch (err) {
      console.error('Errore nel salvataggio della schedule:', err);
      const serverError = err.response?.data?.error || 'Errore durante il salvataggio';
      emitError(serverError);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------- render ---------- */
  if (isSubmitting) {
    return (
      <div className="addschedule-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="addschedule-container">
      {error && <p className="error">{error}</p>}
      {success && <p className="success">{success}</p>}

      <h2>Aggiungi Programmazione</h2>
      <form onSubmit={handleSubmit} className="addschedule-form">
        {/* PROGRAMMA --------------------------------------------------------- */}
        <label>
          Programma:
          <Select
            options={programOptions}
            value={programOptions.find(o => o.value === formData.programId) || null}
            onChange={handleProgramSelect}
            placeholder="Seleziona un programma..."
            isClearable
            required
            classNamePrefix="select"
          />
        </label>

        {/* FLAG RICORRENTE --------------------------------------------------- */}
        <label>
          Ricorrente:
          <input
            type="checkbox"
            name="isRecurring"
            checked={formData.isRecurring}
            onChange={handleChange}
          />
        </label>

        {/* EVENTO ONE-SHOT --------------------------------------------------- */}
        {!formData.isRecurring && (
          <>
            <label>
              Descrizione:
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Descrizione (opzionale)"
              />
            </label>
            <label>
              Data:
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </label>
            <label>
              Inizio:
              <input
                type="time"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                required
              />
            </label>
            <label>
              Fine:
              <input
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
                required
              />
            </label>
            <label>
              Priorità:
              <input
                type="number"
                name="priority"
                min={0}
                value={formData.priority}
                onChange={handleChange}
              />
            </label>
            <label>
              Ospiti:
              <AsyncSelect
                isMulti
                loadOptions={loadPeopleOptions}
                defaultOptions={peopleOptions.slice(0, 20)}
                filterOption={personOptionMatches}
                onChange={opts => handleMultiSelectChange(opts, 'guests')}
                value={peopleOptions.filter(o => formData.guests.includes(o.value))}
                placeholder="Seleziona ospiti..."
                classNamePrefix="select"
              />
            </label>
            <label>
              Argomenti:
              <Select
                isMulti
                options={topicOptions}
                onChange={opts => handleMultiSelectChange(opts, 'topics')}
                value={topicOptions.filter(o => formData.topics.includes(o.value))}
                placeholder="Seleziona argomenti..."
                classNamePrefix="select"
              />
            </label>
          </>
        )}

        

        {/* BLOCCO RICORRENTE ------------------------------------------------- */}
        {formData.isRecurring && (
          <div className="recurring-container">
            <h4>Eventi Ricorrenti</h4>

            {recurringEntries.map((entry, idx) => (
              <div key={idx} className="recurring-entry">
                {/* Giorno ------------------------------------- */}
                <label>
                  Giorno:
                  <select
                    value={entry.dayOfWeek}
                    onChange={e =>
                      handleRecurringEntryChange(idx, 'dayOfWeek', e.target.value)
                    }
                    required
                  >
                    <option value="">Seleziona</option>
                    {daysOptions.map(d => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </label>
                {/* Orari -------------------------------------- */}
                <label>
                  Inizio:
                  <input
                    type="time"
                    value={entry.startTime}
                    onChange={e =>
                      handleRecurringEntryChange(idx, 'startTime', e.target.value)
                    }
                    required
                  />
                </label>
                <label>
                  Fine:
                  <input
                    type="time"
                    value={entry.endTime}
                    onChange={e =>
                      handleRecurringEntryChange(idx, 'endTime', e.target.value)
                    }
                    required
                  />
                </label>
                {/* Descrizione - Priorità --------------------- */}
                <label>
                  Descrizione:
                  <input
                    type="text"
                    value={entry.description}
                    onChange={e =>
                      handleRecurringEntryChange(idx, 'description', e.target.value)
                    }
                    placeholder="Opzionale"
                  />
                </label>
                <label>
                  Priorità:
                  <input
                    type="number"
                    min={0}
                    value={entry.priority}
                    onChange={e =>
                      handleRecurringEntryChange(
                        idx,
                        'priority',
                        parseInt(e.target.value || '0', 10)
                      )
                    }
                  />
                </label>
                {/* Date blacklist ------------------------------ */}
                <label>
                    Date da escludere:
                </label>
                <label>
        
                  <DatePicker
                    multiple
                    
                    format="YYYY-MM-DD"
                    editable={false}
                    value={entry.blacklistDates}
                    inputClass="picker-input"
                    inputProps={{ readOnly: true }}
                    className="bg-dark"
                    onChange={dates =>
                      handleRecurringEntryChange(
                        idx,
                        'blacklistDates',
                        dates.filter(Boolean).map(d => d.format('YYYY-MM-DD'))
                      )
                    }
                  />
                </label>
                {/* Guests / Topics ----------------------------- */}
                <label>
                  Ospiti:
                  <AsyncSelect
                    isMulti
                    loadOptions={loadPeopleOptions}
                    defaultOptions={peopleOptions.slice(0, 20)}
                    filterOption={personOptionMatches}
                    onChange={opts =>
                      handleRecurringMultiSelectChange(idx, 'guests', opts)
                    }
                    value={peopleOptions.filter(o => entry.guests.includes(o.value))}
                    placeholder="Seleziona ospiti..."
                    classNamePrefix="select"
                  />
                </label>
                <label>
                  Argomenti:
                  <Select
                    isMulti
                    options={topicOptions}
                    onChange={opts =>
                      handleRecurringMultiSelectChange(idx, 'topics', opts)
                    }
                    value={topicOptions.filter(o => entry.topics.includes(o.value))}
                    placeholder="Seleziona argomenti..."
                    classNamePrefix="select"
                  />
                </label>

                {/* Rimuovi entry ------------------------------ */}
                {recurringEntries.length > 1 && (
                  <button
                    type="button"
                    className="small-btn remove-btn"
                    onClick={() => removeRecurringEntry(idx)}
                  >
                    Rimuovi
                  </button>
                )}
                <hr />
              </div>
            ))}

            <button
              type="button"
              className="small-btn add-entry-btn"
              onClick={addRecurringEntry}
            >
              Aggiungi Ricorrenza
            </button>
          </div>
        )}

        {/* SUBMIT ------------------------------------------------------------ */}
        <button type="submit" className="submit-btn">
          Aggiungi Programmazione
        </button>
      </form>

      {/* Redirect dopo successo */}
      {success && (
        <button
          onClick={() => navigate('/dashboard')}
          className="redirect-btn"
        >
          Vai alla Dashboard
        </button>
      )}
    </div>
  );
};

export default AddSchedule;
